// ============================================================
// CHARACTER NORMALIZATION - the single choke point that makes
// any stored character shape (legacy Firestore docs, workshop
// JSON, imports) safe for current code. Read-only: callers get
// a NEW object; stored data is never written back.
//
// Guarantees (v6.30.0):
// - skills entries are tiered SkillRating objects; legacy
//   { trained, bonus } migrates bonus → mod
// - dodge + parry entries always exist
// - cumulative tier hygiene: plus20 ⇒ plus10 ⇒ trained
// - the deprecated `bonus` mirror is maintained on dodge/parry
//   (stale multiplayer clients read skills.dodge.bonus)
// - unknown skill keys are dropped
// - idempotent + deterministic (no Date.now/Math.random)
// ============================================================
import { readKillTeamBlock } from '../killTeam/block';
import { readAlwaysOn, readSoloModes } from './soloModes';
import { migrateStances } from './stanceMigration';
import type { Character, CharacterSkills, SkillId, SkillRating } from './types';
import { emptyCharacter } from './factories';
import { getSkillDef } from './skillCatalog';
import { readObjectSpec } from '../objects/objectSpec';

/** The deprecated dodge/parry mirror: tier bonuses + mod. */
export function computeSkillMirrorBonus(r: Omit<SkillRating, 'bonus'>): number {
  return (r.plus10 ? 10 : 0) + (r.plus20 ? 10 : 0) + (r.mod || 0);
}

/** Normalize one raw skill entry (legacy or modern) into a SkillRating. */
function normalizeRating(raw: unknown, withMirror: boolean): SkillRating {
  const r = asRecord(raw);
  const isLegacy = !('plus10' in r) && !('plus20' in r) && !('mod' in r);
  const plus20 = !isLegacy && !!r.plus20;
  // Cumulative tiers: +20 implies +10 implies trained
  const plus10 = plus20 || (!isLegacy && !!r.plus10);
  const trained = plus10 || !!r.trained;
  // A legacy rating kept its whole bonus in `bonus`.
  const rating: SkillRating = { trained, plus10, plus20, mod: numberOr0(isLegacy ? r.bonus : r.mod) };
  if (withMirror) rating.bonus = computeSkillMirrorBonus(rating);
  return rating;
}

/** An object's fields, or none for anything that is not an object. */
function asRecord(raw: unknown): Record<string, unknown> {
  return (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
}

function numberOr0(v: unknown): number {
  return typeof v === 'number' ? v : 0;
}

/** Ratings whose id has a `bonus` mirror maintained for stale clients. */
const MIRRORED: ReadonlySet<string> = new Set(['dodge', 'parry']);

function normalizeSkills(raw: unknown): CharacterSkills {
  const result: Partial<Record<SkillId, SkillRating>> = {};
  for (const [key, value] of Object.entries(asRecord(raw))) {
    if (!getSkillDef(key)) continue; // drop unknown keys
    result[key as SkillId] = normalizeRating(value, MIRRORED.has(key));
  }
  // dodge + parry guaranteed
  for (const id of MIRRORED as Set<SkillId>) {
    if (!result[id]) result[id] = normalizeRating(undefined, true);
  }
  return result as CharacterSkills;
}

/**
 * The talent or trait instances that can be read: objects with a string
 * id. Everything kept is the SAME object, untouched - a rewrite here
 * would differ from what another client stored and the sheet echo would
 * never settle. Repeats and unknown ids (custom entries) are kept.
 */
function normalizeInstances<T>(raw: unknown, idKey: string): T[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((inst) => typeof asRecord(inst)[idKey] === 'string') as T[];
}

/**
 * The team record's kill-team block and leadership, read once where a sheet comes in (plan modes-rework), so every
 * team helper past this point trusts them as typed. A block that is not one, and a leader flag that is not `true`, go.
 */
function readTeamParts(team: NonNullable<Character['team']>): NonNullable<Character['team']> {
  const { killTeam, leads, ...rest } = team;
  const block = readKillTeamBlock(killTeam);
  return { ...rest, ...(block ? { killTeam: block } : {}), ...(leads === true ? { leads: true as const } : {}) };
}

/**
 * The sheet's Always on items and Solo modes, read once here (plan modes-rework): an older sheet's stance groups
 * become them (`migrateStances`, ids kept); a list that reads empty is left off, never written as `[]`.
 */
function withModes(sheet: Character, input: Record<string, unknown>): Character {
  const { alwaysOn: _a, soloModes: _s, stanceGroups: _g, ...rest } = sheet as Character & { stanceGroups?: unknown };
  const raw = migrateStances(input) ?? input;
  const alwaysOn = readAlwaysOn(raw.alwaysOn);
  const soloModes = readSoloModes(raw.soloModes);
  // Spread only when there is something (gotcha 2: Firestore refuses a field that is `undefined`).
  return { ...rest, ...(alwaysOn.length ? { alwaysOn } : {}), ...(soloModes.length ? { soloModes } : {}) };
}

/**
 * Normalize a raw character object. Deterministic and idempotent -
 * safe to call at every ingress (storage load, Firestore sync, import).
 */
export function normalizeCharacter(raw: unknown): Character {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Partial<Character>;
  // Base defaults for any missing top-level field; the input wins elsewhere.
  const base = emptyCharacter(input.id || 'char-normalized');
  const result: Character = { ...base, ...input } as Character;
  result.skills = normalizeSkills(input.skills);
  result.talents = normalizeInstances(input.talents, 'talentId');
  result.traits = normalizeInstances(input.traits, 'traitId');
  // An Object's fields, read defensively; dropped from any other body (2026-10-26).
  if (result.bodyType === 'object') result.objectSpec = readObjectSpec(input.objectSpec);
  else delete result.objectSpec;
  if (input.team) result.team = readTeamParts(input.team);
  // Deterministic timestamps: never fabricate - keep input values or 0
  result.createdAt = typeof input.createdAt === 'number' ? input.createdAt : 0;
  result.updatedAt = typeof input.updatedAt === 'number' ? input.updatedAt : 0;
  return withModes(result, input as Record<string, unknown>);
}

/**
 * Tolerant per-skill accessor for possibly-unnormalized characters
 * (e.g. Tab.sourceCharacter arriving via Firestore target sync or
 * history snapshots, which have no single normalization ingress).
 */
export function getSkillRating(character: Character, skillId: SkillId): SkillRating {
  const raw = (character.skills as Record<string, unknown> | undefined)?.[skillId];
  if (!raw) return { trained: false, plus10: false, plus20: false, mod: 0 };
  return normalizeRating(raw, MIRRORED.has(skillId));
}
